import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { candidateAPI } from '../services/api';
import toast from 'react-hot-toast';
import ResumeUpload from './ResumeUpload';
import { resumeDisplayName } from '../utils/downloadFile';
import { downloadResumeAuthenticated, isGatewayResumeUrl } from '../utils/resumeUrl';
import ProfileCompletionBar from './ProfileCompletionBar';

function asList(value) {
  return Array.isArray(value) ? value.filter(Boolean) : [];
}

function detailsFromProfile(profile) {
  const raw = profile?.resumeExtractedData || {};
  const work = asList(profile?.workExperience);
  const extractedExperience = asList(raw.experience);
  return {
    summary: profile?.summary || raw.summary || '',
    experience: work.length ? work : extractedExperience,
    certifications: asList(raw.certifications),
    projects: asList(raw.projects),
    languages: asList(raw.languages),
    phone: raw?.personalInfo?.phone || '',
  };
}

function detailsFromUpload(raw) {
  return {
    summary: raw?.summary || '',
    experience: asList(raw?.experience),
    certifications: asList(raw?.certifications),
    projects: asList(raw?.projects),
    languages: asList(raw?.languages),
    phone: raw?.personalInfo?.phone || '',
  };
}

function hasResumeDetails(details) {
  if (!details) return false;
  return Boolean(
    details.summary
    || details.phone
    || details.experience.length
    || details.certifications.length
    || details.projects.length
    || details.languages.length
  );
}

function ResumeDetailsPanel({ details }) {
  if (!hasResumeDetails(details)) return null;
  return (
    <div className="bg-white p-4 rounded-lg border border-gray-200 space-y-4">
      <h3 className="text-lg font-semibold text-gray-900">Details from your resume</h3>
      {details.phone && (
        <p className="text-sm text-gray-700"><span className="font-medium">Phone on resume:</span> {details.phone}</p>
      )}
      {details.summary && (
        <div>
          <h4 className="text-sm font-medium text-gray-900 mb-1">Summary</h4>
          <p className="text-sm text-gray-700 whitespace-pre-line">{details.summary}</p>
        </div>
      )}
      {details.experience.length > 0 && (
        <div>
          <h4 className="text-sm font-medium text-gray-900 mb-2">Experience</h4>
          <div className="space-y-3">
            {details.experience.map((job, index) => (
              <div key={`${job.company || 'job'}-${index}`} className="rounded-md border border-gray-200 bg-gray-50 p-3">
                <p className="text-sm font-semibold text-gray-900">{job.position || 'Role'}</p>
                <p className="text-sm text-gray-700">{job.company || 'Company'}</p>
                {(job.startDate || job.endDate) && (
                  <p className="text-xs text-gray-500 mt-1">
                    {job.startDate || 'Start'} – {job.endDate || 'Present'}
                    {job.duration ? ` · ${job.duration}` : ''}
                  </p>
                )}
                {asList(job.responsibilities).length > 0 && (
                  <ul className="mt-2 list-disc pl-5 space-y-1">
                    {asList(job.responsibilities).map((item, respIndex) => (
                      <li key={`${index}-${respIndex}`} className="text-sm text-gray-600">{item}</li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
      {details.projects.length > 0 && (
        <div>
          <h4 className="text-sm font-medium text-gray-900 mb-1">Projects</h4>
          <ul className="list-disc pl-5 space-y-1">
            {details.projects.map((project, index) => (
              <li key={`project-${index}`} className="text-sm text-gray-700">{project}</li>
            ))}
          </ul>
        </div>
      )}
      {details.certifications.length > 0 && (
        <div>
          <h4 className="text-sm font-medium text-gray-900 mb-1">Certifications</h4>
          <ul className="list-disc pl-5 space-y-1">
            {details.certifications.map((item, index) => (
              <li key={`cert-${index}`} className="text-sm text-gray-700">{item}</li>
            ))}
          </ul>
        </div>
      )}
      {details.languages.length > 0 && (
        <div>
          <h4 className="text-sm font-medium text-gray-900 mb-1">Languages</h4>
          <p className="text-sm text-gray-700">{details.languages.join(', ')}</p>
        </div>
      )}
    </div>
  );
}

const EditCandidateProfile = ({ onClose, onSuccess }) => {
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(true);
  const [showResumeUpload, setShowResumeUpload] = useState(false);
  const [resumeUrl, setResumeUrl] = useState(null);
  const [resumeDownloading, setResumeDownloading] = useState(false);
  const [resumeViewLoading, setResumeViewLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    title: '',
    experienceYears: 0,
    education: '',
    skills: '',
    address: '',
    linkedinUrl: '',
    githubUrl: '',
    portfolioUrl: '',
  });
  const [completion, setCompletion] = useState({ percentage: 0, nextHint: '' });
  const [resumeDetails, setResumeDetails] = useState(null);
  

  // Fetch existing profile data
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        setIsFetching(true);
        const response = await candidateAPI.getProfile();
        const profile = response.data;
        
        // Convert skills array to comma-separated string
        const skillsString = Array.isArray(profile.skills) 
          ? profile.skills.join(', ') 
          : '';
        
        setFormData({
          title: profile.title || '',
          experienceYears: profile.experienceYears || 0,
          education: profile.education || '',
          skills: skillsString,
          address: profile.address || '',
          linkedinUrl: profile.linkedinUrl || '',
          githubUrl: profile.githubUrl || '',
          portfolioUrl: profile.portfolioUrl || '',
        });
        setResumeUrl(profile.resumeUrl || profile.resume_url || null);
        setResumeDetails(detailsFromProfile(profile));
        setCompletion({
          percentage: profile.profileCompletionPercentage || 0,
          nextHint: profile.profileCompletionNextHint || '',
        });
        
      } catch (error) {
        console.error('Error fetching profile:', error);
        toast.error('Failed to load profile data');
      } finally {
        setIsFetching(false);
      }
    };

    fetchProfile();
  }, []);

  const handleChange = (e) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? parseInt(value) || 0 : value
    }));
  };

  const handleResumeUploadSuccess = (data) => {
    if (data?.resumeUrl) {
      setResumeUrl(data.resumeUrl);
    }

    const extractedData = data.extractedData;
    const rawData = data.rawParsedData;
    
    if (extractedData || rawData) {
      // Auto-apply ALL extracted data to form immediately
      // Try extractedData first, then fallback to rawData
      const newFormData = {
        skills: extractedData?.skills || (rawData?.skills ? rawData.skills.join(', ') : '') || '',
        education: extractedData?.education || (rawData?.education ? rawData.education.map(edu => 
          `${edu.degree || 'Degree'} in ${edu.fieldOfStudy || 'Field'} from ${edu.institution || 'Institution'}${edu.year ? ` (${edu.year})` : ''}`
        ).join('\n') : '') || '',
        experienceYears: extractedData?.experienceYears || rawData?.totalYearsOfExperience || 0,
        address: extractedData?.address || rawData?.personalInfo?.location || '',
        title:
          extractedData?.title
          || rawData?.jobTitle
          || rawData?.currentRole
          || rawData?.headline
          || (Array.isArray(rawData?.experience) && rawData.experience[0]?.position)
          || '',
        ...(extractedData?.linkedinUrl ? { linkedinUrl: extractedData.linkedinUrl } : {}),
        ...(extractedData?.githubUrl ? { githubUrl: extractedData.githubUrl } : {}),
      };
      setResumeDetails(detailsFromUpload(rawData));
      
      setFormData(prev => ({
        ...prev,
        ...newFormData
      }));
      
      toast.success('Resume processed and profile auto-filled! You can edit any fields as needed.', {
        duration: 5000,
      });
      
      // Close resume upload section
      setShowResumeUpload(false);
    }
  };

  const resumeLabel = resumeDisplayName(
    resumeUrl,
    [user?.firstName, user?.lastName].filter(Boolean).join(' ')
  );

  const downloadResume = async () => {
    if (!resumeUrl) {
      toast.error('No resume available');
      return;
    }
    try {
      setResumeDownloading(true);
      const res = await candidateAPI.getResumeAccessUrl('attachment');
      const url = res.data.url;
      if (isGatewayResumeUrl(url)) {
        await downloadResumeAuthenticated(url, resumeLabel);
        return;
      }
      const link = document.createElement('a');
      link.href = url;
      link.download = resumeLabel;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error downloading resume:', error);
      toast.error(error.response?.data?.detail || 'Could not download resume');
    } finally {
      setResumeDownloading(false);
    }
  };

  const viewResume = async () => {
    if (!resumeUrl) {
      toast.error('No resume available');
      return;
    }
    try {
      setResumeViewLoading(true);
      const res = await candidateAPI.getResumeAccessUrl('inline');
      const url = res.data.url;
      if (isGatewayResumeUrl(url)) {
        await downloadResumeAuthenticated(url);
        return;
      }
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (error) {
      console.error('Error viewing resume:', error);
      toast.error(error.response?.data?.detail || 'Could not open resume');
    } finally {
      setResumeViewLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      await candidateAPI.updateProfile(formData);
      toast.success('Profile updated successfully!');
      if (onSuccess) onSuccess();
      if (onClose) onClose();
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setIsLoading(false);
    }
  };

  if (isFetching) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading profile...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
          <h2 className="text-2xl font-bold text-gray-900">Edit Candidate Profile</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <ProfileCompletionBar
            percentage={completion.percentage}
            nextHint={completion.nextHint}
            compact
          />
          {/* Resume Upload Section */}
          <div className="bg-gradient-to-r from-primary-50 to-blue-50 p-4 rounded-lg border border-primary-200">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <svg className="w-6 h-6 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
                <h3 className="text-lg font-semibold text-gray-900">Quick Fill from Resume</h3>
              </div>
              <button
                type="button"
                onClick={() => setShowResumeUpload(!showResumeUpload)}
                className="text-sm text-primary-600 hover:text-primary-700 font-medium"
              >
                {showResumeUpload ? 'Hide' : 'Upload Resume'}
              </button>
            </div>
            <p className="text-sm text-gray-600 mb-3">
              Save time by uploading your resume. We'll automatically extract and fill your profile with skills, experience, and education!
            </p>
            {showResumeUpload && (
              <div className="bg-white p-4 rounded-lg">
                <ResumeUpload onUploadSuccess={handleResumeUploadSuccess} />
              </div>
            )}
          </div>

          {resumeUrl && (
            <div className="bg-white p-4 rounded-lg border border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Your Resume</h3>
              <p className="text-sm text-gray-600 mb-3 truncate" title={resumeLabel}>
                {resumeLabel}
              </p>
              <div className="flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={() => void viewResume()}
                  disabled={resumeViewLoading || resumeDownloading}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-primary-50 text-primary-700 rounded-md hover:bg-primary-100 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                  {resumeViewLoading ? 'Opening…' : 'View Resume'}
                </button>
                <button
                  type="button"
                  onClick={() => void downloadResume()}
                  disabled={resumeDownloading}
                  className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  {resumeDownloading ? 'Downloading…' : 'Download Resume'}
                </button>
              </div>
            </div>
          )}

          <ResumeDetailsPanel details={resumeDetails} />

          {/* Display Basic Info (Disabled) */}
          <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Basic Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  First Name
                </label>
                <input
                  type="text"
                  value={user.firstName || ''}
                  disabled
                  className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-500 cursor-not-allowed"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Last Name
                </label>
                <input
                  type="text"
                  value={user.lastName || ''}
                  disabled
                  className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-500 cursor-not-allowed"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email Address
                </label>
                <input
                  type="email"
                  value={user.email || ''}
                  disabled
                  className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-500 cursor-not-allowed"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Mobile No
                </label>
                <input
                  type="tel"
                  value={user.phone || ''}
                  disabled
                  className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-500 cursor-not-allowed"
                />
              </div>
            </div>
          </div>

          {/* Editable Fields */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">Professional Details</h3>
            
            {/* Title */}
            <div>
              <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
                Job Title / Profession <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="title"
                name="title"
                value={formData.title}
                onChange={handleChange}
                placeholder="e.g., Software Engineer, Data Analyst, Web Developer"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>

            {/* Experience Years */}
            <div>
              <label htmlFor="experienceYears" className="block text-sm font-medium text-gray-700 mb-1">
                Years of Experience <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                id="experienceYears"
                name="experienceYears"
                value={formData.experienceYears}
                onChange={handleChange}
                min="0"
                max="50"
                placeholder="e.g., 3"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            
            {/* Education Details */}
            <div>
              <label htmlFor="education" className="block text-sm font-medium text-gray-700 mb-1">
                Education Details
                <span className="text-gray-500 text-xs ml-2">(e.g., BE (EEE))</span>
              </label>
              <textarea
                id="education"
                name="education"
                value={formData.education}
                onChange={handleChange}
                rows="4"
                placeholder="Enter your education details&#10;Example:&#10;BE (EEE) - Anna University - 2020&#10;12th - State Board - 2016"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-none"
              />
            </div>

            {/* Skills */}
            <div>
              <label htmlFor="skills" className="block text-sm font-medium text-gray-700 mb-1">
                Skills
                <span className="text-gray-500 text-xs ml-2">(Comma or line separated)</span>
              </label>
              <textarea
                id="skills"
                name="skills"
                value={formData.skills}
                onChange={handleChange}
                rows="4"
                placeholder="Enter your skills&#10;Example: C, Java, C++, Python, React"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-none"
              />
            </div>

            {/* Address */}
            <div>
              <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-1">
                Address
              </label>
              <textarea
                id="address"
                name="address"
                value={formData.address}
                onChange={handleChange}
                rows="3"
                placeholder="Enter your address&#10;Example: 1st Floor, RH, Bangalore"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-none"
              />
            </div>

            <div>
              <label htmlFor="linkedinUrl" className="block text-sm font-medium text-gray-700 mb-1">
                LinkedIn URL
              </label>
              <input
                type="url"
                id="linkedinUrl"
                name="linkedinUrl"
                value={formData.linkedinUrl}
                onChange={handleChange}
                placeholder="https://linkedin.com/in/you"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            <div>
              <label htmlFor="githubUrl" className="block text-sm font-medium text-gray-700 mb-1">
                GitHub URL
              </label>
              <input
                type="url"
                id="githubUrl"
                name="githubUrl"
                value={formData.githubUrl}
                onChange={handleChange}
                placeholder="https://github.com/you"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            <div>
              <label htmlFor="portfolioUrl" className="block text-sm font-medium text-gray-700 mb-1">
                Portfolio URL
              </label>
              <input
                type="url"
                id="portfolioUrl"
                name="portfolioUrl"
                value={formData.portfolioUrl}
                onChange={handleChange}
                placeholder="https://your-portfolio.com"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditCandidateProfile;


